"use client";

import React, { useEffect, useRef, useState } from "react";
import { Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 animate-pulse rounded-md bg-primary-foreground/20" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li data-index={0}>
          <Link to="/HomePage" className="text-primary-foreground/80 transition-colors hover:text-secondary">
            Home
          </Link>
        </li>
        <li data-index={1}>
          <Link to="/OurWorkPage" className="text-primary-foreground/80 transition-colors hover:text-secondary">
            Our Work
          </Link>
        </li>
        <li data-index={2}>
          <Link to="/DonatePage" className="text-primary-foreground/80 transition-colors hover:text-secondary">
            Donate
          </Link>
        </li>
        <li data-index={3}>
          <Link to="/ContactPage" className="text-primary-foreground/80 transition-colors hover:text-secondary">
            Contact
          </Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-primary-foreground/80 transition-colors hover:text-secondary">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function FooterInfo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: MapPin,
    text: "7 Willis Avenue, Sutton, Surrey, SM2 5HS"
  }, {
    icon: Phone,
    text: "+44 7455 323959"
  }, {
    icon: Mail,
    text: "info@farquharfisherfoundation.co.uk"
  }];
  const socialLinks = [{
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }];
  return <section data-section-id="113125" id="footer" className="bg-primary text-primary-foreground pt-16 pb-8">
      <div ref={ref} className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {}
          <div className="flex flex-col space-y-6">
            <Link to="/HomePage" className="inline-block">
              <img src="https://touching-owl.10web.cloud/wp-content/uploads/2026/03/FFF-Logo.png" alt="Farquhar Fisher Foundation" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-primary-foreground/80 leading-relaxed">
              Transforming lives through education by supporting young people in socially and economically disadvantaged areas of Ghana.
            </p>
          </div>

          {}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-secondary font-sans tracking-wide">
              Quick Links
            </h3>
            <MenuProvider menu_id="47">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-secondary font-sans tracking-wide">
              Contact Us
            </h3>
            <ul className="space-y-4">
              {contactDetails.map((item, i) => {
              const Icon = item.icon;
              return <li key={i} data-index={i} className="flex items-start space-x-3">
                    <Icon className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                    <span className="text-primary-foreground/80">{item.text}</span>
                  </li>;
            })}
            </ul>
          </div>

          {}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-secondary font-sans tracking-wide">
              Follow Us
            </h3>
            <div className="flex space-x-4">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground" aria-label={social.label}>
                    <Icon className="h-5 w-5" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-primary-foreground/20" />

        {}
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <p className="text-sm font-medium text-secondary">
            Registered Charity Number: 1214126
          </p>
          <p className="text-sm text-primary-foreground/60">
            &copy; {new Date().getFullYear()} Farquhar Fisher Foundation. All rights reserved.
          </p>
        </div>
      </div>
    </section>;
}